import {assert} from 'assert-ts';
import {ApiFactoryConfig} from '../../functions/types';
import {QueryParameters} from '../../types';
import {IDataApi, IGet} from './../types';
import {httpMethod} from './https';

export const createHttpDataApi = <
  TData extends Record<TIdKey, any>,
  TIdKey extends keyof TData = keyof TData,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TParameters extends QueryParameters = {},
  TPayload = Omit<Record<string, unknown>, TIdKey>
>(
  config: Omit<
    ApiFactoryConfig<TData, TIdKey>,
    'mode' | 'mockData' | 'mockFactory'
  >,
): IDataApi<TData, TIdKey, TParameters, TPayload> => {
  const {baseUrl, endPoint} = config;

  const get: IGet<TData, TIdKey, TParameters> = (
    paramsOrId?: TParameters | TData[TIdKey],
    accessToken?: string,
  ) => {
    if (typeof paramsOrId === 'object' || paramsOrId === undefined) {
      return httpMethod<TData[]>({
        baseUrl,
        endPoint,
        params: getParameters(paramsOrId),
        method: 'GET',
        accessToken: accessToken,
      }).then(data => assert(data));
    }
    const resolvedEndPoint = `${endPoint}/${paramsOrId}`;
    return httpMethod<TData>({
      baseUrl,
      endPoint: resolvedEndPoint,
      method: 'GET',
      accessToken: accessToken,
    }).then(data => [assert(data)]);
  };

  const update = (
    id: string,
    data: Partial<Omit<TData, TIdKey>>,
    accessToken?: string,
  ) => {
    const resolvedEndPoint = `${endPoint}/${id}`;
    return httpMethod<void>({
      baseUrl,
      endPoint: resolvedEndPoint,
      body: data,
      responseAsJson: false,
      method: 'PATCH',
      accessToken: accessToken,
    });
  };

  const remove = (id: string, accessToken?: string) => {
    const resolvedEndPoint = `${endPoint}/${id}`;
    return httpMethod<void>({
      baseUrl,
      endPoint: resolvedEndPoint,
      responseAsJson: false,
      method: 'DELETE',
      accessToken: accessToken,
    });
  };

  const create = (data: TPayload, accessToken?: string): Promise<TData> => {
    return httpMethod<TData>({
      baseUrl,
      endPoint,
      body: data as Record<string, unknown>,
      method: 'POST',
      accessToken: accessToken,
    }).then(data => assert(data));
  };

  return {get, update, remove, create};
};

const getParameters = <TParameters extends QueryParameters>(
  params?: TParameters,
): {[key: string]: string} => {
  if (params) {
    const retVal: {[key: string]: string} = {};
    Object.keys(params).forEach(key => {
      const value = params[key];
      value !== undefined && (retVal[key] = value.toString());
    });
    return retVal;
  }
  return {};
};
